import React, { useState, useEffect } from 'react';
import { userAPI } from '../../api';

const Team15Levels = () => {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [membersByLevel, setMembersByLevel] = useState({});

  useEffect(() => {
    fetchTeamHierarchy();
  }, []);

  // Organize members by level when teamData changes
  useEffect(() => {
    if (teamData && teamData.teamTree) {
      organizeMembersByLevel(teamData.teamTree);
    }
  }, [teamData]);

  const organizeMembersByLevel = (tree) => {
    const levelMap = {};
    
    // Initialize levels 1-15
    for (let i = 1; i <= 15; i++) {
      levelMap[i] = [];
    }

    // Recursive function to collect members by level
    const collectMembers = (members, level) => {
      members.forEach((member) => {
        if (level <= 15) {
          levelMap[level].push({
            _id: member._id,
            name: member.name,
            email: member.email,
            phone: member.phone,
            mySponsorCode: member.mySponsorCode,
            rank: member.rank,
            selfBusiness: member.selfBusiness,
            level: level,
            childrenCount: member.childrenCount,
            activeCount: member.activeCount,
            createdAt: member.createdAt
          });

          if (member.children && member.children.length > 0) {
            collectMembers(member.children, level + 1);
          }
        }
      });
    };

    collectMembers(tree, 1);
    setMembersByLevel(levelMap);
  };

  const fetchTeamHierarchy = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await userAPI.getTeamLevelsHierarchy();
      setTeamData(response.data?.data || null);
    } catch (err) {
      console.error('Error fetching team hierarchy:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load team hierarchy');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E4572E] border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <div className="text-red-600 font-semibold mb-2">Error Loading Team</div>
        <div className="text-red-500 text-sm mb-4">{error}</div>
        <button
          onClick={fetchTeamHierarchy}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!teamData) {
    return <div className="text-center py-12">No data available</div>;
  }

  const currentLevelMembers = membersByLevel[selectedLevel] || [];

  return (
    <div className="w-full px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#0F2F3D] mb-2 flex items-center gap-3">
          <span className="text-4xl">🌳</span>
          15-Level Team Hierarchy
        </h1>
        <p className="text-gray-600">View all members organized by level</p>
      </div>

      {/* User Info */}
      <div className="bg-gradient-to-r from-[#0F2F3D] to-[#E4572E] rounded-xl p-6 text-white mb-8 shadow-lg">
        <h2 className="text-2xl font-bold mb-1">{teamData.user.name}</h2>
        <p className="text-white/80">{teamData.user.email}</p>
        <p className="text-white/70 font-mono text-sm mt-1">{teamData.user.mySponsorCode}</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold mb-1">{teamData.stats.totalMembers}</div>
          <div className="text-blue-100">Total Team Members</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold mb-1">{teamData.stats.activeMembers}</div>
          <div className="text-green-100">Active Members</div>
        </div>
      </div>

      {/* Level Tabs */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <h3 className="text-lg font-bold text-[#0F2F3D] mb-4">Select Level</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2 mb-2">
          {Array.from({ length: 15 }, (_, i) => i + 1).map((level) => (
            <button
              key={level}
              onClick={() => setSelectedLevel(level)}
              className={`px-3 py-3 rounded-lg font-bold transition-all ${
                selectedLevel === level
                  ? 'bg-gradient-to-r from-[#E4572E] to-[#E4572E] text-white shadow-lg scale-105'
                  : membersByLevel[level]?.length > 0
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              disabled={membersByLevel[level]?.length === 0}
            >
              <div className="text-lg">{level}</div>
              <div className="text-xs">{membersByLevel[level]?.length || 0}</div>
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Numbers show count of members at each level. Red = Selected, Blue = Has members, Gray = No members
        </p>
      </div>

      {/* Level Members Table */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
          <h3 className="text-xl font-bold text-[#0F2F3D]">
            Level {selectedLevel} Members
            <span className="text-gray-600 font-normal ml-2">({currentLevelMembers.length})</span>
          </h3>
          <button
            onClick={fetchTeamHierarchy}
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors font-medium"
          >
            Refresh
          </button>
        </div>

        {currentLevelMembers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">#</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">Mobile</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">Sponsor Code</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">Rank</th>
                  <th className="px-4 py-3 text-right font-bold text-gray-700">Business</th>
                  <th className="px-4 py-3 text-center font-bold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-center font-bold text-gray-700">Direct</th>
                  <th className="px-4 py-3 text-center font-bold text-gray-700">Active</th>
                  <th className="px-4 py-3 text-left font-bold text-gray-700">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentLevelMembers.map((member, index) => (
                  <tr key={member._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-bold text-gray-700">{index + 1}</td>
                    <td className="px-4 py-3">
                      <span className="font-bold text-gray-900">{member.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{member.phone}</td>
                    <td className="px-4 py-3 text-gray-600">{member.email}</td>
                    <td className="px-4 py-3 font-mono text-blue-600 text-xs">{member.mySponsorCode}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-bold">
                        {member.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">
                      ₹{(member.selfBusiness || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs font-bold ${
                          member.selfBusiness > 0
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {member.selfBusiness > 0 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-blue-600">
                      {member.childrenCount}
                    </td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">
                      {member.activeCount}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(member.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🌱</div>
            <h4 className="text-lg font-semibold text-gray-700 mb-2">No Members at Level {selectedLevel}</h4>
            <p className="text-gray-500">There are no team members at this level yet.</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <span>📊</span> How to Read the Table
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <p className="font-bold mb-2">Column Information:</p>
            <ul className="space-y-1">
              <li>✓ <span className="font-bold">Direct</span> = Number of direct referrals</li>
              <li>✓ <span className="font-bold">Active</span> = Active members in their downline</li>
              <li>✓ <span className="font-bold">Business</span> = Self business volume</li>
              <li>✓ <span className="font-bold">Status</span> = Active if business &gt; 0</li>
            </ul>
          </div>
          <div>
            <p className="font-bold mb-2">Level Buttons:</p>
            <ul className="space-y-1">
              <li>🔴 <span className="font-bold">Red</span> = Currently selected level</li>
              <li>🔵 <span className="font-bold">Blue</span> = Has members</li>
              <li>⚪ <span className="font-bold">Gray</span> = No members at this level</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 text-center text-xs text-gray-500">
        <p>Click any level button above to view all members at that level.</p>
      </div>
    </div>
  );
};

export default Team15Levels;
